import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Mail, ArrowLeft, MessageSquare, HelpCircle, Shield, Send, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: 'support',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        category: 'support',
        subject: '',
        message: ''
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send message. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'support', label: 'General Support', icon: MessageSquare },
    { value: 'legal', label: 'Privacy & Legal', icon: Shield },
    { value: 'abuse', label: 'Report Abuse', icon: HelpCircle },
    { value: 'business', label: 'Business Inquiry', icon: Mail }
  ];

  return (
    <>
      <SEO
        title="Contact Us"
        description="Get in touch with Instant-Email support team. Contact us for questions, support, feedback, or business inquiries."
        path="/contact"
      />

      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Mail className="w-8 h-8 text-primary" />
              <span className="font-heading text-xl font-semibold text-foreground">Instant-Email</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-foreground hover:text-primary">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </nav>

        {/* Content */}
        <div className="pt-28 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-4xl font-bold text-foreground mb-4">Contact Us</h1>
            <p className="text-xl text-muted-foreground mb-12">
              We're here to help! Get in touch with our team.
            </p>

            {/* Contact Options - Quick Reference */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                  className={`p-4 rounded-lg border transition-all text-center ${formData.category === cat.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-card hover:border-primary/50'
                    }`}
                >
                  <cat.icon className={`w-6 h-6 mx-auto mb-2 ${formData.category === cat.value ? 'text-primary' : 'text-muted-foreground'
                    }`} />
                  <span className={`text-sm font-medium ${formData.category === cat.value ? 'text-primary' : 'text-foreground'
                    }`}>
                    {cat.label}
                  </span>
                </button>
              ))}
            </div>

            {/* Contact Form */}
            <Card className="bg-card border-border mb-8">
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-6">Send us a Message</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Your Name</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        className="bg-input/50 border-transparent focus:border-primary"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Your Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="bg-input/50 border-transparent focus:border-primary"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-md bg-input/50 border-transparent focus:border-primary text-foreground"
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      name="subject"
                      placeholder="What's this about?"
                      value={formData.subject}
                      onChange={handleChange}
                      className="bg-input/50 border-transparent focus:border-primary"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us more about your question or concern..."
                      value={formData.message}
                      onChange={handleChange}
                      className="bg-input/50 border-transparent focus:border-primary min-h-[150px]"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-full bg-primary text-primary-foreground"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-border">
                  <h3 className="font-semibold mb-3">Response Time</h3>
                  <p className="text-sm text-muted-foreground">
                    We typically respond to inquiries within 24-48 hours during business days. For urgent matters
                    related to security or abuse, we aim to respond as quickly as possible.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Additional Info */}
            <Card className="bg-card border-border">
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-4">Direct Email Contacts</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">General Support</h3>
                    <a href="mailto:support@instant-email.org" className="text-primary hover:underline">
                      support@instant-email.org
                    </a>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Privacy & Legal</h3>
                    <a href="mailto:legal@instant-email.org" className="text-primary hover:underline">
                      legal@instant-email.org
                    </a>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Report Abuse</h3>
                    <a href="mailto:abuse@instant-email.org" className="text-primary hover:underline">
                      abuse@instant-email.org
                    </a>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Business Inquiries</h3>
                    <a href="mailto:business@instant-email.org" className="text-primary hover:underline">
                      business@instant-email.org
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
